import { Injectable, inject, signal } from '@angular/core';
import { firstValueFrom } from 'rxjs';
import { SnackbarService } from '../../../core/shared/components/snackbar/snackbar.service';
import { SpinnerService } from '../../../core/shared/services/spinner.service';
import { extractErrorMessage } from '../../../core/shared/util/error.util';
import { ArquivoDocumentoManualDTO } from '../../documento/models/proposta-manual-documento.model';
import { DadosProposta } from '../models/dados-proposta.model';
import { BuscarDadosPropostaUseCase } from '../usecases/buscar-dados-proposta.usecase';
import { SalvarRascunhoDadosPropostaUseCase } from '../usecases/salvar-rascunho-dados-proposta.usecase';
import { FinalizarDadosPropostaUseCase } from '../usecases/finalizar-dados-proposta.usecase';

interface PropostaFinanciamentoState {
  dados: DadosProposta | null;
  loading: boolean;
  salvandoRascunho: boolean;
  finalizando: boolean;
  error: string | null;
}

@Injectable({
  providedIn: 'root'
})
export class PropostaFinanciamentoViewModel {
  private readonly _state = signal<PropostaFinanciamentoState>({
    dados: null,
    loading: false,
    salvandoRascunho: false,
    finalizando: false,
    error: null
  });

  readonly state = this._state.asReadonly();

  private readonly spinnerService = inject(SpinnerService);
  private readonly snackbarService = inject(SnackbarService);

  constructor(
    private readonly buscarDadosPropostaUseCase: BuscarDadosPropostaUseCase,
    private readonly salvarRascunhoDadosPropostaUseCase: SalvarRascunhoDadosPropostaUseCase,
    private readonly finalizarDadosPropostaUseCase: FinalizarDadosPropostaUseCase
  ) {}

  async carregarDadosProposta(matricula: string): Promise<void> {
    this._state.update(s => ({ ...s, loading: true, error: null }));
    this.spinnerService.show();

    try {
      const dados = await firstValueFrom(this.buscarDadosPropostaUseCase.execute(matricula));
      this._state.update(s => ({ ...s, dados, loading: false }));
    } catch (error) {
      const msgErro = extractErrorMessage(error, 'Erro ao carregar dados da proposta!');
      this._state.update(s => ({ ...s, loading: false, error: msgErro }));
      this.snackbarService.error(msgErro);
    } finally {
      this.spinnerService.hide();
    }
  }

  async salvarRascunho(matricula: string, dados: DadosProposta): Promise<boolean> {
    this._state.update(s => ({ ...s, salvandoRascunho: true, error: null }));

    try {
      const dadosSalvos = await firstValueFrom(this.salvarRascunhoDadosPropostaUseCase.execute(matricula, dados));
      this._state.update(s => ({ ...s, dados: dadosSalvos, salvandoRascunho: false }));
      this.snackbarService.success('Rascunho da proposta salvo com sucesso!');
      return true;
    } catch (error) {
      const msgErro = extractErrorMessage(error, 'Erro ao salvar rascunho da proposta!');
      this._state.update(s => ({ ...s, salvandoRascunho: false, error: msgErro }));
      this.snackbarService.error(msgErro);
      return false;
    }
  }

  async finalizarProposta(matricula: string, dados: DadosProposta): Promise<ArquivoDocumentoManualDTO | null> {
    this._state.update(s => ({ ...s, finalizando: true, error: null }));
    this.spinnerService.show();

    try {
      const resposta = await firstValueFrom(this.finalizarDadosPropostaUseCase.execute(matricula, dados));
      this._state.update(s => ({ ...s, finalizando: false }));

      if (resposta?.success === false || resposta?.error) {
        this.snackbarService.error(resposta?.error ?? resposta?.message ?? 'Erro ao finalizar a proposta!');
        return null;
      }

      this.snackbarService.success(resposta?.message ?? 'Proposta finalizada com sucesso!');
      return resposta?.data ?? null;
    } catch (error) {
      const msgErro = extractErrorMessage(error, 'Erro ao finalizar a proposta!');
      this._state.update(s => ({ ...s, finalizando: false, error: msgErro }));
      this.snackbarService.error(msgErro);
      return null;
    } finally {
      this.spinnerService.hide();
    }
  }
}

